/**
 * WebSocket sync client: optimistic local edits, live remote batches,
 * reconnect with sinceRev catch-up, presence, and a 25s heartbeat to keep
 * the App Service front proxy from idling the socket out.
 */
import type { ChangeEntry, WSServerMsg } from "@trip/shared/protocol";
import { store } from "./store.js";

type StatusListener = (s: { online: boolean; presence: string[] }) => void;

let ws: WebSocket | null = null;
let backoff = 1000;
let heartbeat: ReturnType<typeof setInterval> | null = null;
let statusListeners = new Set<StatusListener>();
let onResyncNeeded: (() => void) | null = null;

const opId = () => Math.random().toString(36).slice(2, 10);

function notify() {
  for (const fn of statusListeners) fn({ online: store.online, presence: store.presence });
}

export function onStatus(fn: StatusListener) {
  statusListeners.add(fn);
  fn({ online: store.online, presence: store.presence });
}

export function onResync(fn: () => void) {
  onResyncNeeded = fn;
}

export function connect() {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}/ws?trip=${encodeURIComponent(store.tripId)}`);

  ws.onopen = () => {
    backoff = 1000;
    ws!.send(JSON.stringify({ t: "hello", key: store.key ?? undefined, sinceRev: store.rev }));
    if (heartbeat) clearInterval(heartbeat);
    heartbeat = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "ping" }));
    }, 25_000);
  };

  ws.onmessage = (e) => {
    let msg: WSServerMsg;
    try {
      msg = JSON.parse(e.data);
    } catch {
      return;
    }
    switch (msg.t) {
      case "welcome":
        store.online = true;
        store.role = msg.role;
        store.editor = msg.name;
        store.presence = msg.presence;
        store.rev = Math.max(store.rev, 0);
        notify();
        break;
      case "catchup":
        store.applyChanges(msg.entries, { remote: true, rev: msg.rev });
        break;
      case "changes":
        store.applyChanges(msg.entries, { remote: true, rev: msg.rev, editor: msg.editor });
        break;
      case "presence":
        store.presence = msg.users;
        notify();
        break;
      case "resync":
        onResyncNeeded?.();
        break;
      case "error":
        console.warn("sync error:", msg.message);
        break;
    }
  };

  ws.onclose = () => {
    store.online = false;
    store.presence = [];
    notify();
    if (heartbeat) clearInterval(heartbeat);
    setTimeout(connect, backoff);
    backoff = Math.min(backoff * 2, 30_000);
  };
  ws.onerror = () => ws?.close();
}

/** Optimistically applies + sends one batch. Callers already validated role.
 * Attribution happens server-side (from the key/session) — nothing to send. */
export function sendChanges(entries: ChangeEntry[]) {
  const withIds = entries.map((e) => ({ ...e, opId: opId() }));
  store.applyChanges(withIds, { remote: false });
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ t: "set", changes: withIds }));
  } else {
    // socket down — REST fallback so a momentary blip doesn't lose the edit
    fetch(`/api/trip/${encodeURIComponent(store.tripId)}/state`, {
      method: "POST",
      headers: { "content-type": "application/json", ...(store.key ? { "x-trip-key": store.key } : {}) },
      body: JSON.stringify({ changes: withIds }),
    }).catch(() => {});
  }
}
